import React, { PureComponent, useContext, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { withTranslation, Trans, useTranslation } from 'react-i18next';
import MediaQuery from 'react-responsive'
import { Link } from 'react-router-dom';

import { makeGetCurrentUser, makeGetResolvedProjectUsers } from 'selectors/users';
import { makeGetTask } from 'selectors/tasks';
import { makeGetMilestone } from 'selectors/milestones';
import { makeGetTaskNotifications } from 'selectors/notifications';
import { makeGetSystemMessagesByTypeAndAssociationId } from 'selectors/messages';
import { makeIsSubscribed } from 'selectors/subscriptions';
import { makeGetProject } from 'selectors/projects';

import { removeFile } from 'actions/files';
import { fetchTask, updateTask, moveTask } from 'actions/tasks';
import { showModal } from 'actions/modal';
import { uploadFiles } from 'actions/files';

import checklistAPI from 'redux/checklist/api';

import { trackEvent } from 'services/events-tracker-service';
import { isTempId, scrollTo } from 'utils/helpers';
import { createSlug, copyUrlToClipboard } from 'utils';
import { buildAppUrl } from 'utils/url';
import { getFile } from 'services/store-service';
import eventBus from 'services/event-bus';
import { createAlert } from 'services/alert-service';
import { getUserPreference, setUserPreference } from 'services/user-preferences-service';

import { LocaleContext } from 'context/LocaleContext';
import { PublicPageContext } from 'context/PublicPageContext';

import Spinner from 'components/spinner';
import Subtasks from 'components/subtasks';
import CanUser from 'components/can-user';
import Tooltip from 'components/tooltip';
import TaskNav from 'components/task-nav';
import SubtasksOverview from 'components/subtasks-overview';
import MilestoneSingle from 'components/milestone-single';
import TaskNiceId from 'components/TaskNiceId';
import SubtaskSingle from 'components/SubtaskSingle';
import TaskTaskGroup from 'components/TaskTaskGroup';
import NotificationsReader from 'components/NotificationsReader';
import TaskMinimize from 'components/TaskMinimize';
import LinkedTasks from 'components/LinkedTasks';
import TiedGoals from 'components/TiedGoals';
import ProjectName from 'components/project-name';

import IconArrowLeft from 'icons/icon-arrow-left';
import IconClose from 'icons/icon-close';
import IconExpand from 'icons/icon-expand';
import IconCollapse from 'icons/icon-collapse';

import TaskDiscussion from './TaskDiscussion';
import TaskControls from './TaskControls';
import TaskDependantOn from './TaskDependantOn';
import TaskTopBarActions from './TaskTopBarActions';
import TaskTitle from './TaskTitle';
import TaskCustomFields from './TaskCustomFields';
import TaskDescription from './TaskDescription';
import TaskAttachments from './TaskAttachments';

const EMPTY_ARRAY = [];

class TaskSingle extends PureComponent {
    static contextType = PublicPageContext;
    static defaultProps = {
        shouldChangeUrl: true,
        large: true,
        section: 'subtasks'
    };

    get isPublic() {
        return this.context?.isPublic === true;
    }

    state = {
        name: this.props.task ? this.props.task.name : '',
        currentSection: this.props.section,
        activityVisible: getUserPreference('task_activity_visible') || false,
        collapsed: getUserPreference('content_panel_collapsed') || false,
        currentSubtaskId: this.props.subtaskId
    };

    handleNewComment = () => {
        setTimeout(() => {
            this.scrollToBottom();
        });
    }

    handleDiscussionFocus = () => {
        this.scrollToBottom();
    }
    handleSubtaskClick = subtaskId => {
        if (this.props.withoutSubtaskOpen) {
            return;
        }

        this.setState({
            currentSubtaskId: subtaskId
        });
    }

    handleSubtaskClose = () => {
        this.setState({
            currentSubtaskId: null
        });

        this.props.onSubtaskClose?.();
    }

    handleSubtaskDelete = () => {
        trackEvent('SUBTASK.REMOVE', {
            via: 'Task'
        });

        this.setState({
            currentSubtaskId: null
        });
    }

    handleRemove = () => {
        trackEvent('TASK.REMOVE', {
            via: 'Task'
        });

        this.close();
    }

    close() {
        if (this.props.onBack) {
            this.props.onBack();
            return;
        }

        this.props.onClose && this.props.onClose();
    }

    handleTaskNavClick = sectionId => {
        this.scrollToSection(sectionId);
    }

    handleScroll = () => {
        if (this.autoscrolling) {
            return;
        }

        const scrollTop = this.scrollElement.scrollTop + this.scrollElement.offsetTop + (this.navElement?.clientHeight || 0);
        const nodes = Array.from(this.scrollElement.querySelectorAll('.task-pane'));
        const currentSection = nodes[nodes.findIndex(section => section.offsetTop > scrollTop) - 1];
        const sectionId = currentSection && currentSection.getAttribute('data-section');

        if (this.state.currentSection !== sectionId) {
            this.setState({
                currentSection: sectionId
            });
        }   
    }

    handleCommentClick = () => {
        this.scrollToBottom();
    }

    handleTaskMoveClick = () => {
        if (this.props.task.archived || !this.taskMoveToProjectCtrl) {
            return;
        }

        this.taskMoveToProjectCtrl.show();
    }

    handleToggleComplete = async (completed, taskGroupId) => {
        const newProps = {
            completed,
            task_group: taskGroupId,
        };

        if (completed) {
            newProps.completed_on = new Date();
        }

        await this.updateTask(newProps);

        if (completed) {
            this.close();

            trackEvent('TASK_SINGLE.COMPLETE');
        }
    }

    handleTaskGroupChange = (taskGroupId, projectId) => {
        this.moveToTaskGroup(taskGroupId, projectId);
    }

    handleSizeToggle = () => {
        setUserPreference('content_panel_collapsed', !this.state.collapsed);

        this.setState({
            collapsed: !this.state.collapsed
        });
    }

    handleMilestoneClick = milestoneId => {
        this.setState({
            currentMilestoneId: milestoneId,
            currentSubtaskId: null
        }, () => {
            this.setState({
                subViewVisible: true
            });
        });
    }

    handleTaskClick = taskId => {
        this.setState({
            currentTaskId: taskId,
            currentSubtaskId: null
        }, () => {
            this.setState({
                subViewVisible: true
            });
        });
    }

    handleTaskClose = () => {
        this.setState({
            subViewVisible: false
        }, () => {
            this.setState({
                currentTaskId: null
            });
        });
    }

    handleMilestoneClose = () => {
        this.setState({
            subViewVisible: false
        }, () => {
            this.setState({
                currentMilestoneId: null
            });
        });
    }

    handleIsDependantChange = isDependant => {
        this.setState({ isDependant });
    }

    moveToTaskGroup(taskGroupId, projectId) {
        const { t } = this.props;

        if (projectId && projectId !== this.props.task.project) {
            this.updateTask({
                project: projectId,
                assignees: [],
                milestone: null,
                order: Number.POSITIVE_INFINITY
            }, true);

            this.props.history.replace(`/${this.props.task.project}/task/${this.props.task.id}-${createSlug(this.props.task.name)}`);
        }

        moveTask(this.props.id, {
            type: 'task_group',
            id: taskGroupId
        })
            .then(() => {
                createAlert('success', `<i class="icon material-icons">check_circle</i> ${t('Successfully moved task')}`);
            });
    }

    scrollToSection(sectionId) {
        this.autoscrolling = true;

        this.scrollTo(this.scrollElement.querySelector(`.section-${sectionId}`).offsetTop - this.scrollElement.offsetTop - this.navElement.clientHeight);

        this.setState({
            currentSection: sectionId
        });

        setTimeout(() => {
            this.autoscrolling = false;
        }, 1500);
    }

    scrollToBottom() {
        if (!this.scrollElement) {
            return;
        }

        this.scrollTo(this.scrollElement.scrollHeight);
    }

    scrollTo(position) {
        scrollTo(this.scrollElement, position);
    }

    editTask(taskProps) {
        if ('assignees' in taskProps) {
            trackEvent('TASK.USERS_ASSIGN');
        }

        if ('description' in taskProps) {
            trackEvent('TASK.DESCRIPTION_EDIT');
        }

        if ('due_date' in taskProps) {
            if (this.props.task && !this.props.task.due_date) {
                trackEvent('TASK.DEADLINE_ADD');
            } else if (!taskProps.due_date) {
                trackEvent('TASK.DEADLINE_REMOVE');
            } else {
                trackEvent('TASK.DEADLINE_EDIT');
            }
        }

        this.updateTask(taskProps);
    }

    focus() {
        if (this.nameInputCtrl) {
            this.nameInputCtrl.focus();
        }
    }

    updateTask(taskProps, preventSave = false) {
        return updateTask(this.props.id, taskProps, preventSave);
    }

    attemptFetchTask(force) {
        if (this.isPublic) {
            return;
        }

        if (
            !force && (
                !this.props.id ||
                (this.props.task && !(this.props.task.shouldRefetch && !this.props.task.loading))
            )
        ) {
            return;
        }

        fetchTask(this.props.id);
    }

    init() {
        setTimeout(() => {
            eventBus.emitEvent('task:open');
        }, 400);

        // Change url
        if (!this.props.noRedirect && this.props.shouldChangeUrl && this.props.location.pathname === `${this.props.task.project}/task/${this.props.id}`) {
            this.props.history.replace(`/${this.props.task.project}/task/${this.props.id}-${createSlug(this.props.task.name)}`);
        }
    }

    setNavElement = el => this.navElement = el;
    setNameInputCtrl = ctrl => this.nameInputCtrl = ctrl;

    get isPersonal() {
        return !this.props.task?.task_group && !this.props.task?.task
    }

    get isCompleted() {
        return this.props.task.completed;
    }

    get taskStatusLabel() {
        const { task, t } = this.props;

        if (task.archived) {
            return t('common:Archived');
        }

        if (task.completed) {
            return t('common:Completed');
        }

        return t('common:Archived');
    }

    componentDidMount() {
        this.props.provideController && this.props.provideController({
            focus: () => {
                this.focus();
            }
        });

        if (this.props.id) {
            this.attemptFetchTask();
        }

        if (this.props.task && (this.props.task.error || this.props.task.removed)) {
            this.close();
            return;
        }

        if (this.props.task && !this.props.task.loading) {
            this.init()
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.task && (this.props.task.error || this.props.task.removed)) {
            this.close();
            return;
        }

        if (this.props.task && !this.props.task.loading && (!prevProps.task || prevProps.task.loading)) {
            this.init()
        }

        if (this.props.id !== prevProps.id) {
            this.setState({
                subViewVisible: false
            });
        }

        if (this.props.id) {
            if ((this.props.id !== prevProps.id && this.props.id !== this.props.niceId && prevProps.id !== this.props.niceId) || !prevProps.id) {
                this.attemptFetchTask();
                this.setState({
                    currentMilestoneId: null,
                    currentTaskId: null,
                    currentSubtaskId: this.props.subtaskId
                });
            } else {
                this.attemptFetchTask();
            }
        }

        if (!prevProps.subtaskId && this.props.subtaskId) {
            this.setState({
                currentSubtaskId: this.props.subtaskId
            });
        }

        if (this.props.task && this.props.task.name !== this.state.name) {
            this.setState({
                name: this.props.task.name
            });
        }
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    get large() {
        // if (this.isPublic) {
        //     return false;
        // }

        return this.props.large && !this.state.collapsed;
    }

    get element() {
        const { task } = this.props;

        if (!task) {
            return <Spinner centered={true} />;
        }

        if (!('name' in task) || task.error) {
            return <Spinner centered={true} />;
        }

        const { t } = this.props;

        return (
            <div className={'content-panel' + (this.state.isDependant ? ' dependant' : '')}>
                <div className="content-panel-inner">
                    <div className="content-panel-main">
                        <div className="content-panel-head">
                            <TaskDependantOn id={this.props.id} onMilestoneClick={this.handleMilestoneClick} onTaskClick={this.handleTaskClick} onIsDependantChange={this.handleIsDependantChange} />

                            <div className="content-panel-head-top">
                                <div className="content-panel-head-utils">
                                    {
                                        this.props.onBack ? (
                                            <button className="content-panel-control btn content-panel-back" onClick={this.props.onBack}><IconArrowLeft size="20px" /></button>
                                        ) : null
                                    }

                                    {
                                        this.props.task.nice_id && !this.isPersonal ? (
                                            <span className="nice-id-holder">
                                                <TaskNiceId niceId={this.props.task.nice_id} />
                                            </span>
                                        ) : null
                                    }

                                    <div className={'tag-pill action' + (task.completed ? ' completed' : '')} onClick={this.handleTaskMoveClick}>
                                        {
                                            !task.archived && !task.completed ? (
                                                <TaskTaskGroup
                                                    key={this.props.id}
                                                    id={this.props.id}
                                                    provideController={ctrl => this.taskMoveToProjectCtrl = ctrl}
                                                    nameFallback={this.isPersonal ? t('common:Personal') : null}
                                                    onChange={this.handleTaskGroupChange}
                                                    onConfirm={this.handleToggleComplete}
                                                />
                                            ) : this.taskStatusLabel
                                        }
                                    </div>

                                    {
                                        this.props.task?.project && this.props.currentProject !== this.props.task.project && !this.isPublic ? (
                                            <Tooltip tooltip={<ProjectName id={this.props.task.project} withTooltipHover={false} />}>
                                                <Link
                                                    to={`/${this.props.task.project}/task`}
                                                    onClick={this.props.onClose}
                                                    className="btn btn-xsmall btn-secondary btn-colored"
                                                >{ t('common:goToProject') }</Link>
                                            </Tooltip>
                                        ) : null
                                    }
                                </div>

                                <div className="content-panel-controls">
                                    <TaskTopBarActions
                                        id={this.props.id}
                                        task={this.props.task}
                                        subscribed={this.props.subscribed}
                                        isSmallScreen={this.props.isSmallScreen}
                                        onRemove={this.handleRemove}
                                    />

                                    {
                                        this.props.large && !this.props.inside ? (
                                            <Tooltip tooltip={this.state.collapsed ? t('common:Expand') : t('common:Collapse')} withTooltipHover={false}>
                                                <button className="content-panel-control btn content-panel-size-toggle" onClick={this.handleSizeToggle}>
                                                    {
                                                        this.state.collapsed ? (
                                                            <IconExpand />
                                                        ) : (
                                                            <IconCollapse />
                                                        )
                                                    }
                                                </button>
                                            </Tooltip>
                                        ) : null
                                    }

                                    {
                                        this.props.onClose ? (
                                            <TaskMinimize id={this.props.id} closeTask={this.props.onClose} />
                                        ) : null
                                    }

                                    {
                                        this.props.onClose ? (
                                            <button className="content-panel-control btn content-panel-close" onClick={this.props.onClose}><IconClose /></button>
                                        ) : null
                                    }
                                </div>
                            </div>
                        </div>

                        <div className="content-panel-main-inner">
                            <div className="content-panel-side">
                                <TaskTitle id={this.props.id} task={this.props.task} onToggleComplete={this.handleToggleComplete} />

                                <div className="content-panel-scrollable nice-scroll" ref={element => this.scrollElement = element} onScroll={this.handleScroll}>
                                    <div className="content-panel-block">
                                        <TaskDescription id={this.props.id} createdBy={this.props.task.created_by} description={this.props.task.description} />
                                    </div>

                                    <div className="content-panel-block">
                                        <div className="content-panel-utils">
                                            <TaskControls id={this.props.id} task={this.props.task} isSmallScreen={this.props.isSmallScreen} />
                                        </div>

                                        <TaskCustomFields id={this.props.id} projectId={this.props.task.project} task={this.props.task} fields={this.props.task.fields} />
                                    </div>

                                    {
                                        task.linked_tasks?.length ? (
                                            <LinkedTasks key={this.props.id} taskId={this.props.id} />
                                        ) : null
                                    }

                                    {
                                        !this.isPublic && task.goals?.length ? (
                                            <TiedGoals key={this.props.id} taskId={this.props.id} />
                                        ) : null
                                    }

                                    {
                                        !this.isPersonal ? (
                                            <div className="content-panel-nav-holder" ref={this.setNavElement}>
                                                <TaskNav onClick={this.handleTaskNavClick} current={this.state.currentSection} />
                                            </div>
                                        ) : null
                                    }

                                    <div className="content-panel-body">
                                        <div className="content-panel-body-main">
                                            <div className="content-panel-section section-subtasks" data-section="subtasks">
                                                <SubtasksOverview taskId={this.props.id} />

                                                <div className="section-body">
                                                    <div className="content-panel-subtasks">
                                                        <Subtasks
                                                            projectId={this.props.task.project}
                                                            taskId={this.props.id}
                                                            page={this.props.page}
                                                            onItemClick={!this.props.withoutSubtaskOpen && this.handleSubtaskClick}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <TaskAttachments
                                                key={this.props.id}
                                                id={this.props.id}
                                                task={this.props.task}
                                                projectId={this.props.task.project}
                                                banner={this.props.task.banner}
                                                noRedirect={this.props.noRedirect}
                                                attachments={this.props.task.attachments}
                                                documents={this.props.task.documents}
                                            />

                                            <TaskDiscussion
                                                id={this.props.id}
                                                projectId={this.props.task.project}
                                                withScrollTo={true}
                                                hideOnLarge={true}
                                                commentsCount={this.props.task.comments}
                                                projectUsers={this.props.projectUsers}
                                                onNewComment={this.handleNewComment}
                                                onDiscussionFocus={this.handleDiscussionFocus}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <MediaQuery minWidth={1500}>
                                {
                                    this.large ? (
                                        <div className="content-panel-side">
                                            <TaskDiscussion
                                                id={this.props.id}
                                                projectId={this.props.task.project}
                                                withScrollTo={true}
                                                commentsCount={this.props.task.comments}
                                                projectUsers={this.props.projectUsers}
                                                onNewComment={this.handleNewComment}
                                                onDiscussionFocus={this.handleDiscussionFocus}
                                            />
                                        </div>
                                    ) : null
                                }
                            </MediaQuery>
                        </div>
                    </div>

                    <div className={'content-panel content-panel-subview' + (this.state.currentSubtaskId ? ' visible' : '')}>
                        {
                            this.state.currentSubtaskId ? (
                                <SubtaskSingle
                                    id={this.state.currentSubtaskId}
                                    isSmallScreen={this.props.isSmallScreen}
                                    discussionElement={<TaskDiscussion
                                        id={this.state.currentSubtaskId}
                                        projectId={this.props.task.project}
                                        withScrollTo={false}
                                        projectUsers={this.props.projectUsers}
                                        onNewComment={this.handleNewComment}
                                        onDiscussionFocus={this.handleDiscussionFocus}
                                    />}
                                    onClose={this.handleSubtaskClose}
                                    onDelete={this.handleSubtaskDelete}
                                />
                            ) : null
                        }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className={'content-panel-holder' + (this.props.id ? ' visible' : '') + (this.props.greaterZIndex ? ' greater-z-index' : '') + (this.props.inside ? ' inside' : '') + ((this.state.subViewVisible) ? ' subview-visible' : '') + (this.large ? ' large' : '')}>
                { this.element }

                {
                    this.props.id ? (
                        <React.Fragment>
                            <NotificationsReader notifications={this.props.notifications.map(({ id }) => id)} />

                            {
                                this.state.currentTaskId ? (
                                    <TaskSingleContainer
                                        id={this.state.currentTaskId}
                                        inside={true}
                                        large={this.large}
                                        noRedirect={true}
                                        shouldChangeUrl={false}
                                        onBack={this.handleTaskClose}
                                        onClose={this.props.onClose}
                                    />
                                ) : null
                            }

                            {
                                this.state.currentMilestoneId ? (
                                    <MilestoneSingle
                                        id={this.state.currentMilestoneId}
                                        inside={true}
                                        large={this.large}
                                        onBack={this.handleMilestoneClose}
                                        onClose={this.props.onClose}
                                    />
                                ) : null
                            }
                        </React.Fragment>
                    ) : null
                }
            </div>
        )
    }
};

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getTaskOrSubtask = makeGetTask();
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();
    const getTaskNotifications = makeGetTaskNotifications();
    const getSystemMessagesByTypeAndAssociationId = makeGetSystemMessagesByTypeAndAssociationId();
    const isSubscribed = makeIsSubscribed();
    const getProject = makeGetProject();
    const getResolvedProjectUsers = makeGetResolvedProjectUsers();

    return ({ users, projects, tasks, notifications, messages, subscriptions, subtasks, milestones, appProps }, ownProps) => {
        const argTaskId = ownProps.id;
        const task =  argTaskId && getTaskOrSubtask(tasks.items, argTaskId);
        const openedFromSubtask = Boolean(task?.task);
        const originalId = isTempId((task && task.id) || argTaskId) ? null : (task && task.id) || argTaskId;
        const taskId = task?.task || originalId;
        const projectId = task && task.project ? task.project : ownProps.projectId;
        const project = getProject(projects.items, projectId);

        return {
            id: taskId,
            niceId: argTaskId,
            currentUser: getCurrentUser(users),
            subtaskId: ownProps.subtaskId || (openedFromSubtask && originalId),
            task: taskId === task?.id ? task : getTask(tasks.items, taskId),
            projectId,
            milestone: task && task.milestone && getMilestone(milestones.items, task.milestone),
            notifications: taskId ? getTaskNotifications(notifications.items, taskId, true, true) : EMPTY_ARRAY,
            activities: taskId ? getSystemMessagesByTypeAndAssociationId(messages.items, 'task', taskId) : EMPTY_ARRAY,
            subscribed: taskId && isSubscribed(subscriptions.items, taskId),
            projectUsers: project && !users.items.some(user => user.loading) ? getResolvedProjectUsers(project, users) : EMPTY_ARRAY,
            currentProject: appProps.currentProject

        }
    }
};

const TaskSingleContainer = withRouter(connect(makeMapStateToProps)(withTranslation(['task', 'milestone', 'time-tracking', 'chat', 'common', 'billing', 'file', 'fields', 'notifications', 'recurring', 'labels'])(TaskSingle)));

export default TaskSingleContainer;
